import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useProfile } from '../hooks/useProfile'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Spinner } from '../components/ui/Spinner'
import { ColorPicker } from '../components/ui/ColorPicker'
import { memberColors } from '../styles/tokens'
import { getInitials } from '../lib/utils'

export const ProfilePage = () => {
  const { user } = useAuth()
  const { data: profile, isLoading, update } = useProfile()

  const [displayName, setDisplayName] = useState('')
  const [color,       setColor]       = useState('violet')
  const [avatarUrl,   setAvatarUrl]   = useState('')
  const [saving,      setSaving]      = useState(false)
  const [msg,         setMsg]         = useState('')

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name ?? '')
      setColor(profile.member_color ?? 'violet')
      setAvatarUrl(profile.avatar_url ?? '')
    }
  }, [profile])

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMsg('')
    try {
      await update.mutateAsync({
        display_name: displayName.trim(),
        member_color: color,
        avatar_url:   avatarUrl.trim() || null,
      })
      setMsg('Profile saved!')
    } catch {
      setMsg('Could not save — please try again.')
    }
    setSaving(false)
  }

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>

  const bgColor = memberColors[color ?? 'violet']

  return (
    <div className="px-4 py-5 max-w-lg mx-auto">
      <h1 className="text-xl font-bold text-text-primary mb-6">Profile</h1>

      {/* Avatar preview */}
      <div className="flex flex-col items-center mb-8">
        <div className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-3 shadow-card"
          style={{ backgroundColor: bgColor }}>
          {getInitials(displayName || profile?.display_name || '')}
        </div>
        <p className="text-sm font-semibold text-text-primary">{displayName || profile?.display_name || 'You'}</p>
        <p className="text-xs text-text-muted mt-0.5">{user?.email}</p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <Input
          label="Display name"
          type="text"
          placeholder="Your name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
        />

        <div>
          <ColorPicker label="Member color" value={color} onChange={setColor} />
          <p className="text-xs text-text-muted mt-1">
            Your color appears on tasks, events, and across the app.
          </p>
        </div>

        <Input
          label="Avatar URL (optional)"
          type="url"
          placeholder="https://…"
          value={avatarUrl}
          onChange={(e) => setAvatarUrl(e.target.value)}
          hint="Link to an image to use as your profile picture."
        />

        {msg && (
          <p className={msg.includes('saved') ? 'text-sm text-emerald-600' : 'text-sm text-rose-600'}>
            {msg}
          </p>
        )}

        <Button type="submit" className="w-full" size="lg" disabled={saving}>
          {saving ? <><Spinner size="sm" /> Saving…</> : 'Save profile'}
        </Button>
      </form>
    </div>
  )
}
